/**
 * Local compatibility layer to REMOVE the dependency on Base44.
 * - Keeps the SAME API shape used in src/api/entities.js and src/api/functions.js
 * - Persists data in localStorage so the app works immediately (no backend).
 * - You can later replace storage with real API calls without touching pages.
 */

function load(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}

function save(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {}
}

function uid(prefix='id') {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2,8)}`;
}

// ---- Minimal in-browser DB ----
const DB_KEYS = {
  Lesson: 'RB_Lesson',
  UserProgress: 'RB_UserProgress',
  DailyCheckIn: 'RB_DailyCheckIn',
  LessonFeedback: 'RB_LessonFeedback',
  User: 'RB_UserProfile'
};

// Seed storage if empty (2 lezioni demo, non cambia la UI)
function ensureSeed() {
  const lessons = load(DB_KEYS.Lesson, null);
  if (!lessons) {
    const seed = [
      { id: 'l1', title: 'Respirazione diaframmatica', slug: 'respirazione-diaframmatica', duration_min: 5, created_at: new Date().toISOString() },
      { id: 'l2', title: 'Box breathing 4-4-4-4', slug: 'box-breathing', duration_min: 5, created_at: new Date().toISOString() },
    ];
    save(DB_KEYS.Lesson, seed);
  }
}
ensureSeed();

// ---- Simple filter function (exact match) ----
function applyFilter(items, where = {}) {
  return items.filter((it) =>
    Object.entries(where).every(([k, v]) => (v === undefined || v === null) ? true : it[k] === v)
  );
}

function entityStore(name) {
  const KEY = DB_KEYS[name];
  return {
    async list() {
      return load(KEY, []);
    },
    async filter(where = {}) {
      const all = load(KEY, []);
      return applyFilter(all, where);
    },
    async get(id) {
      const all = load(KEY, []);
      return all.find((x) => x.id === id) || null;
    },
    async create(data) {
      const all = load(KEY, []);
      const rec = { id: uid(name.toLowerCase()), created_at: new Date().toISOString(), ...data };
      all.push(rec);
      save(KEY, all);
      return rec;
    },
    async update(id, patch) {
      const all = load(KEY, []);
      const idx = all.findIndex((x) => x.id === id);
      if (idx === -1) return null;
      all[idx] = { ...all[idx], ...patch, updated_at: new Date().toISOString() };
      save(KEY, all);
      return all[idx];
    },
    async delete(id) {
      const all = load(KEY, []);
      const next = all.filter((x) => x.id !== id);
      save(KEY, next);
      return { ok: true };
    }
  };
}

// ---- Auth facade ----
const auth = {
  async me() {
    let u = load(DB_KEYS.User, null);
    if (!u) {
      // guest iniziale (nessun login esterno)
      u = {
        id: uid('user'),
        email: 'guest@local',
        name: 'Guest',
        setup_completed: false,
        notification_checkin_enabled: false,
        notification_checkin_time: '21:00',
        notification_exercises_enabled: false,
        notification_exercise_time: '10:00',
        created_at: new Date().toISOString(),
      };
      save(DB_KEYS.User, u);
    }
    return u;
  },
  async login(_provider) {
    // finto login (puoi cambiarlo quando mettiamo il backend)
    const u = {
      id: uid('user'),
      email: 'demo@respirarebene.local',
      name: 'Demo User',
      setup_completed: true,
      notification_checkin_enabled: true,
      notification_checkin_time: '21:00',
      notification_exercises_enabled: true,
      notification_exercise_time: '10:00',
      created_at: new Date().toISOString(),
    };
    save(DB_KEYS.User, u);
    return u;
  },
  async logout() {
    localStorage.removeItem(DB_KEYS.User);
    return { ok: true };
  },
  async updateMyUserData(patch) {
    const u = load(DB_KEYS.User, null) || (await this.me());
    const next = { ...u, ...patch, updated_at: new Date().toISOString() };
    save(DB_KEYS.User, next);
    return next;
  }
};

// ---- Functions (stubs) ----
const functions = {
  async googleDriveManager(payload) {
    console.log('[stub] googleDriveManager called with:', payload);
    return { ok: true, mode: 'local', message: 'Drive stubbed (independent mode)' };
  },
  async weeklyRecap(payload) {
    console.log('[stub] weeklyRecap called with:', payload);
    return { ok: true, message: 'Weekly recap stubbed' };
  }
};

export const base44 = {
  auth,
  entities: {
    Lesson: entityStore('Lesson'),
    UserProgress: entityStore('UserProgress'),
    DailyCheckIn: entityStore('DailyCheckIn'),
    LessonFeedback: entityStore('LessonFeedback'),
  },
  functions
};
